import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getSeasons, toggleSeasonActive, countClassesInSeason } from "@/services/seasonService";
import { Season } from "@/types/classTypes";
import { AdminPageScaffold, DataViewScaffold } from "@/components/core";
import { StandardPageHeader } from "@/components/layout/StandardPageHeader";
import AddSeasonDialog from "@/components/seasons/AddSeasonDialog";
import EditSeasonDialog from "@/components/seasons/EditSeasonDialog";
import { SearchInput } from "@/components/ui/SearchInput";
import { Badge } from "@/components/ui/badge";
import { useSearch } from "@/hooks/useSearch";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Calendar,
  CircleDashed,
  CircleDot,
  Search,
  Plus,
  Clock,
  Users,
  Edit2,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

const Seasons: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const organizationId = user?.user_metadata?.organization_id || '';
  
  // Search
  const { searchQuery, debouncedSearchQuery, setSearchQuery, clearSearch, hasSearch } = useSearch();
  
  // State
  const [classCounts, setClassCounts] = useState<Record<string, number>>({});
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingSeasonId, setEditingSeasonId] = useState<string | null>(null);

  // Data fetching
  const { data: seasons = [], isLoading, error, refetch } = useQuery({
    queryKey: ["seasons", organizationId],
    queryFn: () => getSeasons(organizationId),
    enabled: !!organizationId,
  });

  useEffect(() => {
    // Fetch class counts for each season
    const fetchClassCounts = async () => {
      const counts: Record<string, number> = {};
      for (const season of seasons) {
        counts[season.id] = await countClassesInSeason(season.id);
      }
      setClassCounts(counts);
    };

    if (seasons.length > 0) {
      fetchClassCounts();
    }
  }, [seasons]);

  const handleSeasonAdded = () => {
    refetch();
  };

  const handleSeasonUpdated = () => {
    refetch();
  };

  const handleSeasonDeleted = () => {
    refetch();
  };

  const handleToggleActive = async (season: Season) => {
    const success = await toggleSeasonActive(season.id, organizationId, season.is_active);
    if (success) {
      // Invalidate seasons query to get updated data
      queryClient.invalidateQueries({ queryKey: ["seasons"] });
      refetch();
    }
  };

  const handleViewSeason = useCallback((season: Season) => {
    setEditingSeasonId(season.id);
  }, []);


  // Apply search filter with memoization for performance
  const filteredSeasons = useMemo(() => {
    if (!debouncedSearchQuery) return seasons;
    
    const query = debouncedSearchQuery.toLowerCase().trim();
    return seasons.filter(season => 
      season.name.toLowerCase().includes(query)
    );
  }, [seasons, debouncedSearchQuery]);

  // Sort seasons: active first, then by start date (newest first)
  const sortedSeasons = [...filteredSeasons].sort((a, b) => {
    if (a.is_active && !b.is_active) return -1;
    if (!a.is_active && b.is_active) return 1;
    return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
  });

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = format(parseISO(startDate), "MMM d, yyyy");
    const end = format(parseISO(endDate), "MMM d, yyyy");
    return `${start} → ${end}`;
  };

  if (!organizationId) {
    toast.error('Missing organization ID. Please log in again.');
    return null;
  }

  // Card render function
  const renderSeasonCard = (season: Season) => {
    const classCount = classCounts[season.id] || 0;
    
    const handleCardClick = () => {
      setEditingSeasonId(season.id);
    };
    
    return (
      <Card 
        className="h-full transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 hover:border-primary/30 group cursor-pointer"
        onClick={handleCardClick}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                season.is_active 
                  ? 'bg-green-50 text-green-600' 
                  : 'bg-gray-50 text-gray-400'
              }`}>
                {season.is_active ? (
                  <CircleDot className="h-6 w-6" />
                ) : (
                  <CircleDashed className="h-6 w-6" />
                )}
              </div>
              <div>
                <CardTitle className="text-lg font-semibold group-hover:text-primary transition-colors duration-200 leading-tight">
                  {season.name}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {formatDateRange(season.start_date, season.end_date)}
                </p>
              </div>
            </div>
            
            <Badge variant={season.is_active ? "success" : "muted"} className="shrink-0">
              {season.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Registration Window */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-muted-foreground">
                Registration Period
              </span>
            </div>
            <p className="text-sm text-blue-700">
              {formatDateRange(season.registration_start, season.registration_end)}
            </p>
          </div>

          {/* Options */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-muted-foreground">Options</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {season.registration_fee_enabled && (
                <Badge variant="info" className="text-xs">Registration Fee</Badge>
              )}
              {season.tuition_prepay_enabled && (
                <Badge variant="info" className="text-xs">Tuition Prepay</Badge>
              )}
              {!season.registration_fee_enabled && !season.tuition_prepay_enabled && (
                <span className="text-xs text-muted-foreground">None enabled</span>
              )}
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 text-center transition-all duration-200 hover:scale-105 cursor-pointer">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Users className="h-4 w-4 text-blue-500" />
              </div>
              <div className="text-lg font-semibold text-blue-700">{classCount}</div>
              <div className="text-xs text-blue-600">
                {classCount === 1 ? 'Class' : 'Classes'}
              </div>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Calendar className="h-4 w-4 text-purple-500" />
              </div>
              <div className="text-lg font-semibold text-purple-700">
                {season.is_active ? 'Current' : 'Inactive'}
              </div>
              <div className="text-xs text-purple-600">Status</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-border/50">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleActive(season);
              }}
            >
              {season.is_active ? "Deactivate" : "Activate"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                setEditingSeasonId(season.id);
              }}
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Table render function
  const renderSeasonTableRow = (season: Season) => {
    const classCount = classCounts[season.id] || 0;
    
    const handleRowClick = (e: React.MouseEvent) => {
      // Don't trigger row click if clicking on interactive elements
      if ((e.target as HTMLElement).closest('button, [role="button"]')) {
        return;
      }
      handleViewSeason(season);
    };
    
    return (
      <>
        <td className="px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer" onClick={handleRowClick}>
          <div className="flex items-center gap-2">
            {season.is_active ? (
              <CircleDot className="h-4 w-4 text-green-500" />
            ) : (
              <CircleDashed className="h-4 w-4 text-gray-400" />
            )}
            <span className="font-medium">{season.name}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm hover:bg-muted/50 transition-colors cursor-pointer" onClick={handleRowClick}>
          {formatDateRange(season.start_date, season.end_date)}
        </td>
        <td className="px-4 py-3 text-sm hover:bg-muted/50 transition-colors cursor-pointer" onClick={handleRowClick}>
          {formatDateRange(season.registration_start, season.registration_end)}
        </td>
        <td className="px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer" onClick={handleRowClick}>
          <div className="flex flex-wrap gap-1">
            {season.registration_fee_enabled && (
              <Badge variant="info" className="text-xs">Registration Fee</Badge>
            )}
            {season.tuition_prepay_enabled && (
              <Badge variant="info" className="text-xs">Tuition Prepay</Badge>
            )}
          </div>
        </td>
        <td className="px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer" onClick={handleRowClick}>
          <Badge variant="muted" className="text-xs">
            {classCount} {classCount === 1 ? "class" : "classes"}
          </Badge>
        </td>
        <td className="px-4 py-3 text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActive(season);
            }}
          >
            {season.is_active ? "Deactivate" : "Activate"}
          </Button>
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setEditingSeasonId(season.id);
              }}
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>
        </td>
      </>
    );
  };

  return (
    <>
      <StandardPageHeader
        title="Seasons Management"
        subtitle="Organize your classes into seasonal terms and manage registration periods"
        icon={Calendar}
        actions={
          <Button
            size="sm"
            onClick={() => setIsAddDialogOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Season
          </Button>
        }
      />
      
      <AdminPageScaffold
        loading={isLoading}
        error={error}
        isEmpty={seasons.length === 0 && !searchQuery}
        emptyState={
          <div className="text-center py-16">
            <div className="mx-auto w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-orange-500" />
            </div>
            <h3 className="text-xl font-medium mb-2">No seasons yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Seasons help you organize classes into time periods like semesters, terms, or special programs.
            </p>
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Your First Season
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Data View */}
          <DataViewScaffold
            items={sortedSeasons}
            views={['card', 'table']}
            defaultView="card"
            renderCard={renderSeasonCard}
            renderTableRow={renderSeasonTableRow}
            renderTableHeader={() => (
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Dates</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Registration</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Options</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Classes</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            )}
            emptyState={
              <div className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  {hasSearch ? "No seasons found" : "No seasons yet"}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {hasSearch 
                    ? `No seasons found matching "${searchQuery}"`
                    : "Create your first season to get started organizing classes into time periods"
                  }
                </p>
                {hasSearch ? (
                  <button 
                    onClick={clearSearch}
                    className="text-primary hover:text-primary/80 text-sm"
                  >
                    Clear search
                  </button>
                ) : (
                  <Button
                    onClick={() => setIsAddDialogOpen(true)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Season
                  </Button>
                )}
              </div>
            }
            storageKey="seasons-view-preference"
            cardColumns={{ sm: 1, md: 2, lg: 3, xl: 4 }}
          />
        </div>
      </AdminPageScaffold>

      {/* Dialogs - Always rendered outside AdminPageScaffold */}
      <AddSeasonDialog 
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSeasonAdded={handleSeasonAdded}
      />

      {editingSeasonId && (
        <EditSeasonDialog 
          season={seasons.find(s => s.id === editingSeasonId)!}
          onSeasonUpdated={() => {
            handleSeasonUpdated();
            setEditingSeasonId(null);
          }}
          onSeasonDeleted={() => {
            handleSeasonDeleted();
            setEditingSeasonId(null);
          }}
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setEditingSeasonId(null);
            }
          }}
        />
      )}
    </>
  );
};

export default Seasons;